/**
 * SupportEmailPage — Bandeja del canal de Email de atención al cliente
 * (Fase 2). Sondeo IMAP cada 5 min, hilos agrupados, tabs por estado,
 * filtros automáticos de auto-cierre.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOrganization } from '@clerk/clerk-react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Inbox, Mail, RefreshCw, Send, ShieldAlert, Paperclip,
  Sparkles, CheckCircle2, Circle, XCircle, Loader2, Pencil, X,
  Download, ImagePlus, Filter, Trash2, Search,
} from 'lucide-react';
import { CustomerThreadsBanner } from '../components/CustomerThreadsBanner';

const MAX_ATTACHMENTS = 5;
const MAX_ATTACH_BYTES = 5 * 1024 * 1024;
const ATTACH_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,application/pdf';

function fileToAttachment(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ filename: file.name, data_b64: (reader.result || '').split(',')[1] || '' });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function downloadAttachment(orgId, messageId, attachmentId, filename) {
  try {
    const r = await api.get(
      `/api/support-email/attachments/${messageId}/${attachmentId}?organization_id=${orgId}`,
      { responseType: 'blob' }
    );
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'adjunto';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch {
    toast.error('No se pudo descargar el adjunto');
  }
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function cleanSubject(subject) {
  return (subject || '').replace(/^(re:\s*)+/i, '').trim() || '(sin asunto)';
}

const STATE_LABELS = { pending: 'Pendiente', answered: 'Respondido', closed: 'Cerrado' };

// ── AttachmentImage ─────────────────────────────────────────────
function AttachmentImage({ orgId, messageId, attachmentId, filename }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    api.get(
      `/api/support-email/attachments/${messageId}/${attachmentId}?organization_id=${orgId}`,
      { responseType: 'blob' }
    )
      .then(r => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(r.data);
        setSrc(objectUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [orgId, messageId, attachmentId]);

  if (failed) {
    return (
      <span className="text-[11px] text-red-400 flex items-center gap-1"
        data-testid={`attachment-image-error-${attachmentId}`}>
        <Paperclip size={10} /> {filename} (no disponible)
      </span>
    );
  }
  if (!src) {
    return (
      <div className="w-28 h-28 bg-gray-800/70 rounded-lg animate-pulse"
        data-testid={`attachment-image-loading-${attachmentId}`} />
    );
  }
  return (
    <a href={src} target="_blank" rel="noreferrer"
      data-testid={`attachment-image-${attachmentId}`}>
      <img src={src} alt={filename}
        className="max-w-[220px] max-h-[220px] rounded-lg border border-gray-700 hover:opacity-90 transition-opacity cursor-zoom-in" />
    </a>
  );
}

// ── PendingAttachmentChips ──────────────────────────────────────
function PendingAttachmentChips({ files, onRemove, testIdPrefix }) {
  if (!files.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {files.map((f, i) => (
        <span key={i} data-testid={`${testIdPrefix}-chip-${i}`}
          className="flex items-center gap-1 text-[11px] text-gray-300 bg-gray-800 border border-gray-700 rounded px-2 py-1">
          <Paperclip size={10} /> {f.filename}
          <button type="button" onClick={() => onRemove(i)}
            data-testid={`${testIdPrefix}-remove-${i}`}
            className="text-gray-500 hover:text-red-400 ml-0.5">
            <X size={11} />
          </button>
        </span>
      ))}
    </div>
  );
}

// ── ThreadRow ───────────────────────────────────────────────────
const STATE_BADGE_STYLES = {
  pending: 'bg-amber-500/20 text-amber-300',
  answered: 'bg-emerald-500/20 text-emerald-300',
  closed: 'bg-gray-700 text-gray-400',
};

function ThreadRow({ thread, selected, onClick, showStateBadge }) {
  const senderDisplay = thread.from_name
    ? `${thread.from_name} <${thread.from_email || ''}>`
    : thread.from_email || '';
  return (
    <button
      type="button"
      data-testid={`support-email-thread-${thread.thread_id}`}
      onClick={onClick}
      className={`w-full text-left p-3 border-b border-gray-800/70 hover:bg-gray-800/40 transition-colors ${
        selected ? 'bg-gray-800/70 border-l-2 border-l-amber-500' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-white truncate flex items-center gap-1.5">
          {!thread.sender_verified && (
            <ShieldAlert size={12} className="text-red-400 flex-shrink-0"
              data-testid={`unverified-badge-${thread.thread_id}`} />
          )}
          {cleanSubject(thread.subject)}
        </span>
        <span className="text-[11px] text-gray-500 flex-shrink-0">{timeAgo(thread.updated_at)}</span>
      </div>
      <p className="text-xs text-gray-400 truncate mt-0.5"
        data-testid={`thread-sender-${thread.thread_id}`}>
        {senderDisplay}
      </p>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <p className="text-xs text-gray-600 truncate">{thread.last_message}</p>
        {showStateBadge && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${STATE_BADGE_STYLES[thread.state] || 'bg-gray-800 text-gray-500'}`}
            data-testid={`thread-state-badge-${thread.thread_id}`}>
            {STATE_LABELS[thread.state] || thread.state}
          </span>
        )}
      </div>
    </button>
  );
}

// ── MessageBubble ───────────────────────────────────────────────
function MessageBubble({ msg, orgId }) {
  const isAgent = msg.is_agent_reply;
  const images = (msg.attachments || []).filter(a => (a.content_type || '').startsWith('image/'));
  const files = (msg.attachments || []).filter(a => !(a.content_type || '').startsWith('image/'));
  const senderLabel = isAgent
    ? `Tú → ${msg.to_email || ''}`
    : msg.from_name
      ? `${msg.from_name} <${msg.from_email || ''}>`
      : msg.from_email || '';
  return (
    <div className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}
      data-testid={`support-email-msg-${msg.message_id}`}>
      <div className={`max-w-[75%] min-w-0 rounded-xl p-3 ${
        isAgent
          ? 'bg-amber-600/20 border border-amber-500/30'
          : 'bg-gray-800 border border-gray-700'
      }`}>
        <p className={`text-[10px] mb-1.5 truncate ${
          isAgent ? 'text-amber-400/70' : 'text-gray-500'
        }`}>
          {senderLabel}
        </p>
        {!isAgent && !msg.sender_verified && (
          <div className="flex items-center gap-1.5 text-red-400 text-[11px] mb-1.5"
            data-testid="sender-unverified-warning">
            <ShieldAlert size={11} /> Remitente no verificado (SPF/DKIM/DMARC)
          </div>
        )}
        <p className="text-sm text-gray-100 whitespace-pre-wrap break-words">{msg.text_body}</p>
        {msg.blocked_attachments?.length > 0 && (
          <p className="text-[11px] text-red-400 mt-2">
            Adjunto bloqueado: {msg.blocked_attachments.join(', ')}
          </p>
        )}
        {images.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {images.map(a => (
              <AttachmentImage key={a.attachment_id} orgId={orgId}
                messageId={msg.message_id} attachmentId={a.attachment_id} filename={a.filename} />
            ))}
          </div>
        )}
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {files.map(a => (
              <button key={a.attachment_id} type="button"
                onClick={() => downloadAttachment(orgId, msg.message_id, a.attachment_id, a.filename)}
                data-testid={`attachment-${a.attachment_id}`}
                className="flex items-center gap-1 text-[11px] text-indigo-300 bg-gray-900/50 hover:bg-gray-900 px-2 py-1 rounded">
                <Download size={10} /> {a.filename}
              </button>
            ))}
          </div>
        )}
        <p className="text-[10px] text-gray-500 mt-1.5">{timeAgo(msg.created_at)}</p>
      </div>
    </div>
  );
}

// ── FilterRulesModal ─────────────────────────────────────────────
function FilterRulesModal({ orgId, onClose, rules, onReload, onApply }) {
  const [filterType, setFilterType] = useState('email');
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  const addRule = async () => {
    if (!value.trim()) { toast.error('Introduce un valor para el filtro'); return; }
    setSaving(true);
    try {
      await api.post(`/api/support-email/filter-rules?organization_id=${orgId}`, {
        filter_type: filterType,
        value: value.trim().toLowerCase(),
      });
      toast.success('Regla añadida');
      setValue('');
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al añadir la regla');
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      await api.delete(`/api/support-email/filter-rules/${ruleId}?organization_id=${orgId}`);
      toast.success('Regla eliminada');
      onReload();
    } catch {
      toast.error('Error al eliminar la regla');
    }
  };

  const applyToPending = async () => {
    setApplying(true);
    try {
      const r = await api.post(`/api/support-email/filter-rules/apply-to-pending?organization_id=${orgId}`);
      const { closed, checked } = r.data;
      toast.success(`${closed} hilo(s) cerrado(s) de ${checked} revisados`);
      onReload();
      if (onApply) onApply();
    } catch {
      toast.error('Error al aplicar los filtros');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      data-testid="filter-rules-modal">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Filter size={16} className="text-amber-400" /> Filtros automáticos
          </h3>
          <button data-testid="close-filter-rules-btn" onClick={onClose}
            className="text-gray-500 hover:text-gray-300">
            <X size={18} />
          </button>
        </div>
        <p className="text-xs text-gray-500">
          Los emails cuyo remitente o asunto coincida con una regla se moverán a{' '}
          <span className="text-gray-300 font-medium">Cerrado</span> automáticamente.
        </p>

        {/* Add form */}
        <div className="flex gap-2">
          <select
            data-testid="filter-rule-type-select"
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm text-gray-300 focus:outline-none focus:border-amber-500"
          >
            <option value="email">Email remitente</option>
            <option value="subject">Contiene en asunto</option>
          </select>
          <input
            data-testid="filter-rule-value-input"
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={filterType === 'email' ? 'newsletter@empresa.com' : 'unsubscribe'}
            className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500"
            onKeyDown={e => e.key === 'Enter' && addRule()}
          />
          <button
            data-testid="add-filter-rule-btn"
            onClick={addRule}
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded px-3 py-2 text-sm whitespace-nowrap flex items-center gap-1.5"
          >
            {saving ? <RefreshCw size={14} className="animate-spin" /> : 'Añadir'}
          </button>
        </div>

        {/* Rules list */}
        {rules.length === 0 ? (
          <p className="text-xs text-gray-600 text-center py-4">No hay filtros configurados</p>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {rules.map(rule => (
              <div key={rule.rule_id}
                className="flex items-center justify-between bg-gray-800 rounded-lg px-3 py-2"
                data-testid={`filter-rule-${rule.rule_id}`}>
                <span className="text-xs text-gray-300 flex items-center gap-2">
                  <span className="text-amber-400/70 text-[10px] uppercase font-mono">
                    {rule.filter_type === 'email' ? 'email' : 'asunto'}
                  </span>
                  <span className="text-gray-200">{rule.value}</span>
                </span>
                <button
                  onClick={() => deleteRule(rule.rule_id)}
                  data-testid={`delete-filter-rule-${rule.rule_id}`}
                  className="text-gray-500 hover:text-red-400 transition-colors ml-2">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Apply retroactively */}
        {rules.length > 0 && (
          <div className="pt-2 border-t border-gray-800">
            <button
              data-testid="apply-filters-to-pending-btn"
              onClick={applyToPending}
              disabled={applying}
              className="w-full flex items-center justify-center gap-2 bg-amber-600/20 hover:bg-amber-600/30 border border-amber-600/40 text-amber-400 rounded-lg px-3 py-2 text-xs font-medium transition-colors disabled:opacity-50"
            >
              {applying ? <RefreshCw size={13} className="animate-spin" /> : null}
              {applying ? 'Aplicando filtros...' : 'Aplicar filtros a emails pendientes'}
            </button>
            <p className="text-xs text-gray-600 mt-1.5 text-center">
              Cierra retroactivamente los hilos pendientes que coincidan con las reglas
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── ComposeModal ─────────────────────────────────────────────────
function ComposeModal({ orgId, onClose, onSent }) {
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const handleFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (attachments.length + picked.length > MAX_ATTACHMENTS) {
      toast.error(`Máximo ${MAX_ATTACHMENTS} adjuntos por email`); return;
    }
    const tooBig = picked.find(f => f.size > MAX_ATTACH_BYTES);
    if (tooBig) { toast.error(`"${tooBig.name}" supera el límite de 5MB`); return; }
    const converted = await Promise.all(picked.map(fileToAttachment));
    setAttachments(prev => [...prev, ...converted]);
  };

  const submit = async () => {
    if (!toEmail.trim() || !toEmail.includes('@')) {
      toast.error('Introduce un email de destino válido'); return;
    }
    if (!subject.trim()) { toast.error('El asunto no puede estar vacío'); return; }
    if (!text.trim()) { toast.error('El mensaje no puede estar vacío'); return; }
    setSending(true);
    try {
      const r = await api.post(`/api/support-email/threads/new?organization_id=${orgId}`, {
        to_email: toEmail.trim(), subject: subject.trim(), text: text.trim(), attachments,
      });
      toast.success('Email enviado');
      if (r.data.blocked_attachments?.length > 0) {
        toast.warning(`Adjuntos bloqueados: ${r.data.blocked_attachments.join(', ')}`);
      }
      onSent(r.data.thread_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al enviar el email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      data-testid="compose-email-modal">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Pencil size={16} className="text-amber-400" /> Nuevo email
          </h3>
          <button data-testid="close-compose-modal-btn" onClick={onClose}
            className="text-gray-500 hover:text-gray-300">
            <X size={18} />
          </button>
        </div>
        <div>
          <label className="text-gray-400 text-xs block mb-1">Para</label>
          <input data-testid="compose-to-email-input" value={toEmail}
            onChange={e => setToEmail(e.target.value)} placeholder="cliente@ejemplo.com"
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500" />
        </div>
        <div>
          <label className="text-gray-400 text-xs block mb-1">Asunto</label>
          <input data-testid="compose-subject-input" value={subject}
            onChange={e => setSubject(e.target.value)} placeholder="Asunto del email"
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500" />
        </div>
        <div>
          <label className="text-gray-400 text-xs block mb-1">Mensaje</label>
          <textarea data-testid="compose-body-textarea" value={text}
            onChange={e => setText(e.target.value)} rows={6} placeholder="Escribe tu mensaje..."
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 resize-none" />
        </div>
        <div>
          <input ref={fileInputRef} type="file" multiple accept={ATTACH_ACCEPT}
            className="hidden" data-testid="compose-attachment-input" onChange={handleFiles} />
          <button type="button" onClick={() => fileInputRef.current?.click()}
            data-testid="compose-attach-file-btn"
            className="text-xs text-gray-400 hover:text-amber-400 flex items-center gap-1.5">
            <ImagePlus size={13} /> Adjuntar archivo o imagen
          </button>
          <PendingAttachmentChips files={attachments} testIdPrefix="compose-attachment"
            onRemove={(i) => setAttachments(prev => prev.filter((_, idx) => idx !== i))} />
        </div>
        <div className="flex justify-end gap-2">
          <button data-testid="cancel-compose-btn" onClick={onClose}
            className="text-xs px-3 py-2 rounded text-gray-400 hover:text-gray-200">
            Cancelar
          </button>
          <button data-testid="send-compose-email-btn" onClick={submit} disabled={sending}
            className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-lg px-4 py-2 text-sm flex items-center gap-1.5">
            {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />} Enviar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────
export default function SupportEmailPage() {
  const { organization } = useOrganization();
  const orgId = organization?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [showFilterRules, setShowFilterRules] = useState(false);
  const [filterRules, setFilterRules] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = búsqueda inactiva
  const [searching, setSearching] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const searchDebounceRef = useRef(null);
  const replyFileInputRef = useRef(null);

  const loadThreads = useCallback(async () => {
    if (!orgId) return;
    try {
      const r = await api.get(`/api/support-email/threads?organization_id=${orgId}&state=${activeTab}`);
      setThreads(r.data.threads || []);
      setLoadError(null);
    } catch (e) {
      console.error('[SupportEmail] Error cargando hilos:', e);
      setLoadError(e?.response?.data?.message || e?.message || 'Error al cargar los emails');
    } finally { setLoading(false); }
  }, [orgId, activeTab]);

  const loadFilterRules = useCallback(async () => {
    if (!orgId) return;
    try {
      const r = await api.get(`/api/support-email/filter-rules?organization_id=${orgId}`);
      setFilterRules(r.data.rules || []);
    } catch { /* silent */ }
  }, [orgId]);

  const runSearch = useCallback(async (q) => {
    if (!orgId) return;
    const term = q.trim();
    if (!term) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const r = await api.get(`/api/support-email/threads`, {
        params: { organization_id: orgId, search: term },
      });
      setSearchResults(r.data.threads || []);
    } catch {
      toast.error('Error al buscar');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [orgId]);

  const onSearchInputChange = (val) => {
    setSearchQuery(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => runSearch(val), 350);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  };

  useEffect(() => {
    loadThreads();
    loadFilterRules();
    const t = setInterval(loadThreads, 30000);
    return () => clearInterval(t);
  }, [loadThreads, loadFilterRules]);

  // Deep-link: ?thread=<id>
  useEffect(() => {
    const wanted = searchParams.get('thread');
    if (!wanted) return;
    openThread(wanted);
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.delete('thread');
      return p;
    }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openThread = async (id) => {
    setSelectedId(id);
    setThreadLoading(true);
    setReplyText('');
    setReplyAttachments([]);
    try {
      const r = await api.get(`/api/support-email/threads/${id}?organization_id=${orgId}`);
      setThread(r.data);
    } catch { toast.error('Error al cargar el hilo'); } finally { setThreadLoading(false); }
  };

  const handleReplyFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (replyAttachments.length + picked.length > MAX_ATTACHMENTS) {
      toast.error(`Máximo ${MAX_ATTACHMENTS} adjuntos por email`); return;
    }
    const tooBig = picked.find(f => f.size > MAX_ATTACH_BYTES);
    if (tooBig) { toast.error(`"${tooBig.name}" supera el límite de 5MB`); return; }
    const converted = await Promise.all(picked.map(fileToAttachment));
    setReplyAttachments(prev => [...prev, ...converted]);
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      const r = await api.post(
        `/api/support-email/threads/${selectedId}/reply?organization_id=${orgId}`,
        { text: replyText.trim(), attachments: replyAttachments }
      );
      toast.success('Respuesta enviada');
      if (r.data.blocked_attachments?.length > 0) {
        toast.warning(`Adjuntos bloqueados: ${r.data.blocked_attachments.join(', ')}`);
      }
      setReplyText('');
      setReplyAttachments([]);
      openThread(selectedId);
      loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al enviar');
    } finally { setSending(false); }
  };

  const setThreadState = async (state) => {
    try {
      await api.post(
        `/api/support-email/threads/${selectedId}/state?organization_id=${orgId}`,
        { state }
      );
      const labels = { closed: 'Email cerrado', answered: 'Marcado como respondido', pending: 'Movido a pendiente' };
      toast.success(labels[state] || 'Estado actualizado');
      // Navegar fuera del hilo actual si el nuevo estado no coincide con el tab activo
      // (p.ej. cerrar un email desde la bandeja de pendientes → limpiar el panel)
      if (state !== activeTab) {
        setSelectedId(null);
        setThread(null);
      } else {
        setThread(prev => ({ ...prev, state }));
      }
      loadThreads();
    } catch { toast.error('Error al cambiar el estado'); }
  };

  const pendingCount = threads.filter(t => t.state === 'pending').length;
  const answeredCount = threads.filter(t => t.state === 'answered').length;
  const closedCount = threads.filter(t => t.state === 'closed').length;
  const isSearching = searchResults !== null;
  const filteredThreads = isSearching ? searchResults : threads;

  const TABS = [
    { key: 'pending', label: 'Pendiente', count: pendingCount },
    { key: 'answered', label: 'Respondido', count: answeredCount },
    { key: 'closed', label: 'Cerrado', count: closedCount },
  ];

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-gray-950" data-testid="support-email-page">
      {/* Header */}
      <div className="p-5 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-xl font-semibold text-white flex items-center gap-2">
            <Inbox size={20} className="text-amber-400" /> Email de Soporte
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">Buzón de atención al cliente — configúralo en Conectores.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="open-filter-rules-btn"
            onClick={() => setShowFilterRules(true)}
            className="text-gray-400 hover:text-amber-400 transition-colors p-2 rounded-lg hover:bg-gray-800"
            title="Filtros automáticos"
          >
            <Filter size={16} />
          </button>
          <button
            data-testid="open-compose-email-btn"
            onClick={() => setShowCompose(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1.5"
          >
            <Pencil size={14} /> Nuevo email
          </button>
        </div>
      </div>

      {/* Buscador — busca por email, nombre o asunto en TODO el histórico, sin límite de tab */}
      <div className="px-5 py-3 border-b border-gray-800 flex-shrink-0">
        <div className="relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            data-testid="support-email-search-input"
            value={searchQuery}
            onChange={e => onSearchInputChange(e.target.value)}
            placeholder="Buscar por email, nombre o asunto..."
            className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-9 pr-8 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500"
          />
          {searching && (
            <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 animate-spin" />
          )}
          {!searching && searchQuery && (
            <button data-testid="clear-search-btn" onClick={clearSearch}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCompose && (
        <ComposeModal
          orgId={orgId}
          onClose={() => setShowCompose(false)}
          onSent={(newThreadId) => { setShowCompose(false); loadThreads(); openThread(newThreadId); }}
        />
      )}
      {showFilterRules && (
        <FilterRulesModal
          orgId={orgId}
          onClose={() => setShowFilterRules(false)}
          rules={filterRules}
          onReload={loadFilterRules}
          onApply={loadThreads}
        />
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Left: Thread list */}
        <div className="w-80 border-r border-gray-800 flex flex-col overflow-hidden flex-shrink-0">
          {/* Tabs */}
          <div className="flex border-b border-gray-800 flex-shrink-0">
            {TABS.map(tab => (
              <button
                key={tab.key}
                data-testid={`tab-${tab.key}`}
                onClick={() => { clearSearch(); setLoading(true); setActiveTab(tab.key); }}
                className={`flex-1 py-2.5 text-[11px] font-medium transition-colors flex items-center justify-center gap-1 ${
                  !isSearching && activeTab === tab.key
                    ? 'text-amber-400 border-b-2 border-amber-400'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                    !isSearching && activeTab === tab.key
                      ? 'bg-amber-500/20 text-amber-300'
                      : 'bg-gray-800 text-gray-500'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Thread list */}
          <div className="flex-1 overflow-y-auto">
            {isSearching && (
              <p className="px-3 py-2 text-[11px] text-gray-500 border-b border-gray-800/70" data-testid="search-results-count">
                {filteredThreads.length} resultado(s) para "{searchQuery}"
              </p>
            )}
            {loading && !isSearching ? (
              <div className="p-4 text-xs text-gray-500 flex items-center gap-2">
                <Loader2 size={13} className="animate-spin" /> Cargando...
              </div>
            ) : filteredThreads.length === 0 ? (
              <div className="p-4 text-xs text-gray-600 text-center mt-4" data-testid="threads-empty-state">
                {isSearching
                  ? 'Sin resultados para tu búsqueda.'
                  : loadError
                    ? <span className="text-red-400" data-testid="threads-load-error">Error al cargar: {loadError}</span>
                    : threads.length === 0
                      ? 'Sin emails todavía. Conecta tu buzón en Conectores.'
                      : `No hay emails ${STATE_LABELS[activeTab]?.toLowerCase() || ''}.`}
              </div>
            ) : (
              filteredThreads.map(t => (
                <ThreadRow key={t.thread_id} thread={t} selected={selectedId === t.thread_id}
                  showStateBadge={isSearching}
                  onClick={() => openThread(t.thread_id)} />
              ))
            )}
          </div>
        </div>

        {/* Right: Thread detail */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
              <div className="text-center">
                <Mail size={32} className="mx-auto mb-2 text-gray-700" />
                Selecciona un email
              </div>
            </div>
          ) : threadLoading || !thread ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 size={20} className="animate-spin text-gray-500" />
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-gray-800 flex items-center justify-between flex-shrink-0">
                <div className="min-w-0">
                  <h2 className="text-white font-medium text-sm truncate">
                    {cleanSubject(thread.subject)}
                  </h2>
                </div>
                <div className="flex gap-1.5 flex-shrink-0 ml-2">
                  <button data-testid="state-answered-btn" onClick={() => setThreadState('answered')}
                    className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                      thread.state === 'answered'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}>
                    <CheckCircle2 size={11} /> Respondido
                  </button>
                  <button data-testid="state-closed-btn" onClick={() => setThreadState('closed')}
                    className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                      thread.state === 'closed'
                        ? 'bg-gray-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}>
                    <XCircle size={11} /> Cerrar
                  </button>
                  <button data-testid="state-pending-btn" onClick={() => setThreadState('pending')}
                    className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                      thread.state === 'pending'
                        ? 'bg-amber-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}>
                    <Circle size={11} /> Pendiente
                  </button>
                </div>
              </div>

              {thread.customer_id && (
                <CustomerThreadsBanner
                  customerId={thread.customer_id}
                  currentType="support_email"
                  currentId={selectedId}
                />
              )}

              <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-3">
                {thread.messages.map(m => (
                  <MessageBubble key={m.message_id} msg={m} orgId={orgId} />
                ))}
              </div>

              {thread.ai_draft && (
                <div className="mx-4 mb-2 p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/30"
                  data-testid="ai-draft-box">
                  <p className="text-[11px] text-indigo-300 flex items-center gap-1 mb-1">
                    <Sparkles size={11} /> Borrador sugerido por IA (revisa antes de enviar)
                  </p>
                  <p className="text-sm text-gray-200 whitespace-pre-wrap">{thread.ai_draft}</p>
                  <button data-testid="use-ai-draft-btn" onClick={() => setReplyText(thread.ai_draft)}
                    className="mt-2 text-xs text-indigo-300 underline hover:text-indigo-200">
                    Usar este borrador
                  </button>
                </div>
              )}

              <div className="p-4 border-t border-gray-800 flex-shrink-0 space-y-2">
                <div className="flex gap-2">
                  <textarea
                    data-testid="support-email-reply-textarea"
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    placeholder="Escribe tu respuesta..."
                    rows={2}
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 resize-none"
                  />
                  <button data-testid="send-support-email-reply-btn" onClick={sendReply}
                    disabled={sending || !replyText.trim()}
                    className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-lg px-4 flex items-center gap-1.5 text-sm">
                    {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />} Enviar
                  </button>
                </div>
                <div>
                  <input ref={replyFileInputRef} type="file" multiple accept={ATTACH_ACCEPT}
                    className="hidden" data-testid="reply-attachment-input" onChange={handleReplyFiles} />
                  <button type="button" onClick={() => replyFileInputRef.current?.click()}
                    data-testid="reply-attach-file-btn"
                    className="text-xs text-gray-500 hover:text-amber-400 flex items-center gap-1.5">
                    <ImagePlus size={13} /> Adjuntar archivo o imagen
                  </button>
                  <PendingAttachmentChips files={replyAttachments} testIdPrefix="reply-attachment"
                    onRemove={(i) => setReplyAttachments(prev => prev.filter((_, idx) => idx !== i))} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
